import { fetchEventSource } from '@microsoft/fetch-event-source';

import { ENV } from '../config/env.js';

import type { TokenProvider } from './client.js';
import type { JobEvent, JobEventName, JobRecord } from './types.js';

export interface SseHandlers {
  onEvent(event: JobEvent): void;
  onError?(err: unknown): void;
}

const TERMINAL: ReadonlySet<JobEventName> = new Set(['completed', 'failed']);

/**
 * Subscribe to `/api/jobs/{id}/events`. Returns an abort handle. We use
 * `fetch-event-source` so we can attach the bearer token (the native
 * `EventSource` API doesn't support custom headers).
 */
export function subscribeJobEvents(
  jobId: string,
  getToken: TokenProvider,
  handlers: SseHandlers,
): () => void {
  const controller = new AbortController();
  void fetchEventSource(`${ENV.apiBaseUrl}/api/jobs/${encodeURIComponent(jobId)}/events`, {
    signal: controller.signal,
    openWhenHidden: true,
    fetch: async (input, init): Promise<Response> => {
      const token = await getToken();
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${token}`);
      return fetch(input, { ...init, headers });
    },
    onmessage: (msg) => {
      const event = parseSseEvent(msg.event, msg.data);
      handlers.onEvent(event);
      if (TERMINAL.has(event.type)) controller.abort();
    },
    onerror: (err) => {
      handlers.onError?.(err);
      // Returning a number lets fetch-event-source retry with backoff;
      // throwing aborts. We throw on terminal events only.
      throw err;
    },
  }).catch((err: unknown) => {
    if (controller.signal.aborted) return;
    handlers.onError?.(err);
  });
  return () => controller.abort();
}

export function parseSseEvent(name: string, data: string): JobEvent {
  const eventName = (name || 'snapshot') as JobEventName;
  if (!data) return { type: eventName };
  try {
    const parsed = JSON.parse(data) as Record<string, unknown>;
    if (eventName === 'log') {
      return {
        type: 'log',
        jobId: typeof parsed.jobId === 'string' ? parsed.jobId : undefined,
        message: typeof parsed.message === 'string' ? parsed.message : undefined,
      };
    }
    if (eventName === 'snapshot') {
      return { type: 'snapshot', job: parsed as unknown as JobRecord };
    }
    const job = (parsed as { job?: JobRecord }).job;
    return { type: eventName, job };
  } catch {
    return { type: eventName };
  }
}
